// Pick a file that is ALREADY in the user's repository, for the dashboard
// composer's "Browse repository…" action.
//
// The point of this component is what it does NOT do: it never uploads. The
// composer's other path (`addFiles`) calls `uploadFile`, which writes a new
// `file_{uuid}` row and a new S3 object — so re-attaching a part you already
// have used to mint a duplicate record and duplicate bytes. Choosing here hands
// back an existing `fileId` and nothing is created.
//
// Built on `Modal` and modelled on `SearchOverlay` — the repo's existing
// searchable-list-over-a-dialog — so the focus trap, capture-phase Escape,
// scroll lock and backdrop close are inherited rather than re-implemented, and
// the keyboard model is the one users already met in Cmd/Ctrl+K.
//
// `FileRepository` is deliberately untouched and stays the MANAGEMENT surface
// (upload / rename / delete / download). Selecting and administering are two
// different jobs, and its rename and delete controls have no business sitting
// under a cursor that is hunting for a file to attach.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import { fileIcon, formatSize } from './FileRepository';
import { listFiles } from '../api/repository';
import { classifyAttachment } from '../constants/attachments';
import { relativeTime } from '../pages/dashboardGroups';
import './FilePickerModal.css';

export const KINDS = [
  { value: 'all', label: 'All' },
  { value: 'cad', label: 'CAD' },
  { value: 'gcode', label: 'G-code' },
];

/**
 * The rows to show, ranked.
 *
 * Two rules beyond the obvious substring match. A file the composer could not
 * accept is never listed at all — `classifyAttachment` returns null for the
 * `.npz` phase fields and `.json` configs that share this repository, and
 * offering one only to have the composer refuse it is a worse answer than not
 * offering it. And ranking is by WHERE the match falls, as in `searchProjects`:
 * a name starting with the query is nearly always the file meant.
 *
 * Deliberately UNCAPPED — a picker that silently hides files is the same defect
 * as the unpaginated list endpoint this feature exposed. The list scrolls.
 *
 * Exported for its own test; the ordering is the part worth pinning.
 */
export function searchFiles(files, query, kind = 'all') {
  const q = (query || '').trim().toLowerCase();
  const scored = [];
  for (const file of files || []) {
    const fileKind = classifyAttachment(file?.fileName);
    if (!fileKind) continue;
    if (kind !== 'all' && fileKind !== kind) continue;

    const name = (file.fileName || '').toLowerCase();
    let rank;
    if (!q) rank = 0;
    else if (name.startsWith(q)) rank = 0;
    else if (name.includes(q)) rank = 1;
    else continue;
    scored.push({ file, kind: fileKind, rank });
  }
  // Stable within a rank, so the endpoint's createdAt-desc order survives.
  scored.sort((a, b) => a.rank - b.rank);
  return scored;
}

export default function FilePickerModal({ open, onClose, onSelect, excludeFileIds }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('all');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const excluded = useMemo(() => new Set(excludeFileIds || []), [excludeFileIds]);
  const results = useMemo(() => searchFiles(files, query, kind), [files, query, kind]);

  // Reopening asks the question fresh, and re-fetches: a file may have been
  // uploaded from the Files dialog since this was last open.
  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setKind('all');
    setActive(0);
    setError(null);
    setLoading(true);
    let alive = true;
    listFiles()
      .then((list) => {
        if (alive) setFiles(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (alive) setError(err?.message || 'Could not load your files.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open]);

  // Clamp rather than reset: typing narrows the list under a highlight that may
  // now be past the end, and resetting to 0 on every keystroke fights the arrows.
  useEffect(() => {
    setActive((cur) => (cur < results.length ? cur : Math.max(0, results.length - 1)));
  }, [results.length]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const choose = useCallback(
    (entry) => {
      // An already-attached file is listed so its absence isn't a puzzle, but
      // picking it again would add a second chip for one fileId.
      if (!entry || excluded.has(entry.file.fileId)) return;
      onClose?.();
      onSelect(entry.file);
    },
    [excluded, onClose, onSelect]
  );

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((cur) => (results.length ? (cur + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((cur) => (results.length ? (cur - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[active]);
    }
  }

  return (
    <Modal open={open} onClose={onClose} className="file-picker" labelledBy="file-picker-input">
      <div className="file-picker-head">
        <span className="file-picker-icon" aria-hidden="true">
          🔍
        </span>
        <input
          id="file-picker-input"
          ref={inputRef}
          type="text"
          className="file-picker-input"
          placeholder="Search your files"
          aria-label="Search your files"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button type="button" className="file-picker-close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="file-picker-kinds" role="group" aria-label="Filter by file type">
        {KINDS.map((k) => (
          <button
            key={k.value}
            type="button"
            className={`file-picker-kind${kind === k.value ? ' active' : ''}`}
            aria-pressed={kind === k.value}
            onClick={() => setKind(k.value)}
          >
            {k.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="file-picker-empty">Loading your files…</p>
      ) : error ? (
        <p className="file-picker-empty file-picker-error">{error}</p>
      ) : results.length === 0 ? (
        <p className="file-picker-empty">
          {query.trim()
            ? `No files match "${query.trim()}"`
            : kind === 'all'
              ? 'No CAD or G-code files in your repository yet.'
              : `No ${kind === 'cad' ? 'CAD' : 'G-code'} files in your repository yet.`}
        </p>
      ) : (
        <ul className="file-picker-results" role="listbox" aria-label="Your files">
          {results.map((entry, i) => {
            const isAdded = excluded.has(entry.file.fileId);
            return (
              <li key={entry.file.fileId}>
                <button
                  type="button"
                  className={`file-picker-result${i === active ? ' active' : ''}${isAdded ? ' added' : ''}`}
                  role="option"
                  aria-selected={i === active}
                  aria-disabled={isAdded || undefined}
                  // Pointer move, not enter: entering re-highlights when the list
                  // scrolls under a stationary cursor, which fights the arrows.
                  onMouseMove={() => setActive(i)}
                  onClick={() => choose(entry)}
                >
                  <span className="file-picker-result-icon" aria-hidden="true">
                    {fileIcon(null, entry.file.fileName)}
                  </span>
                  <span className="file-picker-result-name">{entry.file.fileName}</span>
                  <span className="file-picker-result-meta">
                    {isAdded
                      ? 'Added'
                      : `${formatSize(entry.file.fileSize)} · ${relativeTime(entry.file.createdAt)}`}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
